import React, {type PropsWithChildren, type ReactElement, useEffect, useMemo, useState} from 'react'

import type {LngLat, MapLocationRequest, Margin} from '../../components/map'
import {Map as YandexMap, MapClusterer, MapControl, MapControls, MapFeature, MapZoomControl} from '../../components/map'
import {useConfig} from '../../config'
import {AddressPin} from './components/AddressPin/AddressPin'
import {FullSizeControl} from './components/FullSizeControl/FullSizeControl'
import type {ClusterFeature, ControlsPosition, MapPoint} from './types'
import {getCluster, getLocation, mapPointToFeature} from './utils'

export type MapProps = {
  point?: MapPoint
  polygon?: LngLat[]
  points?: MapPoint[]
  isStatic?: boolean
  marker?: (feature: ClusterFeature) => ReactElement
  controlsPosition?: ControlsPosition
  onFullSize?: VoidFunction
  margin?: Margin
  withZoomControls?: boolean
  brand: string
}

const DEFAULT_MARKER = (): ReactElement => <></>

export function TariffsMap({
  polygon,
  points,
  marker,
  isStatic = false,
  children,
  controlsPosition = 'bottom right',
  onFullSize,
  point,
  margin,
  withZoomControls = true,
}: PropsWithChildren<MapProps>): React.ReactElement<MapProps> {
  const mapLocation = useMemo(() => getLocation({point, points, polygon}), [point, points, polygon])
  const [location, setLocation] = useState<MapLocationRequest>(mapLocation)
  const {mapLang, brand} = useConfig()

  useEffect(() => {
    setLocation(mapLocation)
  }, [mapLocation])

  return (
    <YandexMap
      behaviors={isStatic ? [] /*передаём пустой массив поведений если карта должна быть статична*/ : undefined}
      brand={brand}
      lang={mapLang}
      location={location}
      margin={margin}
    >
      {points && (
        <MapClusterer<ClusterFeature>
          cluster={getCluster(isStatic ? undefined : setLocation)}
          features={points?.map(mapPointToFeature)}
          gridSize={128}
          marker={marker || DEFAULT_MARKER}
        />
      )}

      {polygon && (
        <MapFeature
          geometry={{type: 'Polygon', coordinates: [polygon]}}
          style={{
            stroke: [{color: 'rgb(22, 156, 220)', width: 2}],
            fill: 'rgba(66, 198, 250, 0.2)',
          }}
        />
      )}

      <MapControls position={controlsPosition}>
        {!isStatic && withZoomControls && (
          <MapControl transparent>
            <MapZoomControl />
          </MapControl>
        )}

        {onFullSize && (
          <MapControl transparent>
            <FullSizeControl onFullSize={onFullSize} />
          </MapControl>
        )}
      </MapControls>

      {point && <AddressPin address={point.title} coordinates={point.coordinates} />}

      {children}
    </YandexMap>
  )
}
